import React, { useState, useEffect, useRef } from 'react';
import { Days, DaysTranslations } from "../../constants/index.js";
import check from "../../assets/check.svg";
import DoubleCalendar from "../../components/comman/dataPicker.js";
import Button from "../../components/comman/button.js";
import Modal from "../../components/comman/modal.js";
import Select from "react-select";
import Toast from "../../components/Toast/toast.js";
import ConfirmationModal from "../../components/Modal/ConfirmationModal.jsx";
import ErrorDisplay from "../../components/comman/ErrorDisplay.js";
import {useAuth} from "../../utils/authContext.js";
import {fetchAvailableDays, fetchExceptionDays, deleteExceptionDay} from "../../services/schedule-service.js";
import {getChefData} from "../../services/profile-service.js";
import { useLanguage } from "../../utils/languageContext.js";
import { getTranslation } from "../../utils/translate.js";
import { useDarkMode } from "../../utils/darkModeContext.js";
import LoadingSpinner from "../../components/LoadingSpinner/loadingSpinner.js";
import CompanySchedules from "../../components/Schedule/CompanySchedules.js";

const Schedule = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedDays, setSelectedDays] = useState([]);
    const [isExceptionModalOpen, setIsExceptionModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const [exceptionIdToDelete, setExceptionIdToDelete] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const toastRef = useRef(null);
    const { chefData, setChefData, refreshChefData, isLoading: isAuthLoading } = useAuth();
    const { language } = useLanguage();
    const { isDarkMode } = useDarkMode();
    const hasFetchedRef = useRef(false);
    const chefAvailabilityExceptionDayDtoList = chefData?.chefAvailabilityExceptionDayDtoList || [];
    const chefAvailableDtoList = chefData?.chefAvailableDtoList || [];

    // Fetch schedule data on mount to trigger error handling (standardized pattern)
    useEffect(() => {
        // Wait for auth loading to complete
        if (isAuthLoading) return;
        
        // If already fetched, don't fetch again
        if (hasFetchedRef.current) return;
        
        const fetchScheduleData = async () => {
            hasFetchedRef.current = true;
            setLoading(true);
            setError(null);
            
            try {
                // Refresh chef data to get latest schedule information
                // This will trigger error handling if there's a network issue
                await refreshChefData();
                setError(null);
            } catch (err) {
                console.error("Error fetching schedule data:", err);
                setError(err);
            } finally {
                setLoading(false);
            }
        };

        // Only fetch if we have chefData (meaning auth is successful)
        if (chefData) {
            fetchScheduleData();
        }
    }, [isAuthLoading, chefData, refreshChefData]);

    // Sync selectedDays with chefAvailableDtoList when it changes (e.g., after update)
    useEffect(() => {
        if (!chefAvailableDtoList || chefAvailableDtoList.length === 0) {
            // If no available days, clear selected days
            if (selectedDays.length > 0) {
                setSelectedDays([]);
            }
            return;
        }

        const mappedDays = chefAvailableDtoList.map(day => ({
            dayOfTheWeek: day.dayOfTheWeek,
            timeFrom: day.timeFrom || "09:00",
            timeTo: day.timeTo || "18:00",
            isAvailable: true
        }));

        // Only update if the data has actually changed (compare by content, not just reference)
        const currentDaysStr = JSON.stringify(selectedDays.sort((a, b) => a.dayOfTheWeek - b.dayOfTheWeek));
        const mappedDaysStr = JSON.stringify(mappedDays.sort((a, b) => a.dayOfTheWeek - b.dayOfTheWeek));
        
        if (currentDaysStr !== mappedDaysStr) {
            setSelectedDays(mappedDays);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [chefAvailableDtoList]);

    const handleSelectDay = (index) => {
        setSelectedDays((prevSelected) => {
            const existingIndex = prevSelected.findIndex(d => d.dayOfTheWeek === index);

            if (existingIndex !== -1) {
                return prevSelected.filter(d => d.dayOfTheWeek !== index);
            } else {
                return [...prevSelected, { 
                    dayOfTheWeek: index, 
                    isAvailable: true,
                    timeFrom: "09:00",
                    timeTo: "18:00"
                }];
            }
        });
    };

    const handleTimeChange = (index, field, value) => {
        setSelectedDays(prevSelected => {
            const updatedDays = prevSelected.map(day => 
                day.dayOfTheWeek === index 
                    ? { ...day, [field]: value } 
                    : day
            );
            
            // Clear validation error for this day when time changes
            const dayData = updatedDays.find(d => d.dayOfTheWeek === index);
            if (dayData) {
                const timeFrom = dayData.timeFrom || "09:00";
                const timeTo = dayData.timeTo || "18:00";
                if (timeFrom < timeTo) {
                    setValidationErrors(prev => {
                        const newErrors = { ...prev };
                        delete newErrors[index];
                        return newErrors;
                    });
                }
            }
            
            return updatedDays;
        });
    };

    const updateChefData = async () => {
        try {
            // Use refreshChefData from context which updates the auth hook's chefData
            // This ensures the component re-renders with updated data
            if (refreshChefData) {
                await refreshChefData();
            } else {
                // Fallback: manually refresh and update
                const updatedChefData = await getChefData(true);
                if (updatedChefData) {
                    setChefData(updatedChefData);
                }
            }
        } catch (error) {
            console.error("Failed to refresh chef data:", error);
        }
    };
    
    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            
            // Validate time ranges
            const errors = {};
            let hasErrors = false;
            
            for (const day of selectedDays) {
                const timeFrom = day.timeFrom || "09:00";
                const timeTo = day.timeTo || "18:00";
                if (timeFrom >= timeTo) {
                    errors[day.dayOfTheWeek] = getTranslation('schedule.invalidTimeRange', language) || 
                        `Invalid time range: "From" time must be before "To" time`;
                    hasErrors = true;
                }
            }
            
            if (hasErrors) {
                setValidationErrors(errors);
                toastRef.current?.addToast(
                    getTranslation('schedule.timeValidationError', language) || 
                    "Please fix the time range errors before saving",
                    "error"
                );
                setIsSubmitting(false);
                return;
            }
            
            // Clear any previous errors
            setValidationErrors({});
            
            // Backend ChefAvailableDto only requires: dayOfTheWeek, timeFrom, timeTo
            // isAvailable is not part of the schema for available days
            const dataToSend = selectedDays.map((day) => ({
                dayOfTheWeek: day.dayOfTheWeek, // Required: 1=Monday, 2=Tuesday, ..., 7=Sunday
                timeFrom: day.timeFrom || "09:00", // Required: Format "HH:mm"
                timeTo: day.timeTo || "18:00", // Required: Format "HH:mm"
            }));

            const response = await fetchAvailableDays(dataToSend);
            if (response && response.length) {
                // Refresh chef data to update UI
                await updateChefData();
                toastRef.current?.addToast(
                    getTranslation('schedule.scheduleUpdated', language) || 
                    "Schedule updated successfully",
                    "success"
                );
                setIsModalOpen(false);
            } else {
                // If response is empty, still refresh to ensure UI is up to date
                await updateChefData();
            }
        } catch (error) {
            console.error("Failed to update available days:", error);
            toastRef.current?.addToast(
                getTranslation('schedule.updateError', language) || 
                "Failed to update schedule. Please try again.",
                "error"
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    const onDateSelect = async (dates) => {
        if (!dates?.length) return;

        try {
            setIsSubmitting(true);
            // Format dates to yyyy-MM-dd string format as required by backend
            const dataToSend = dates.map(date => {
                const dateStr = typeof date === 'string' 
                    ? date 
                    : `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
                return {
                    exceptionDate: dateStr, // Backend expects LocalDate string in yyyy-MM-dd format
                    timeFrom: "00:01", // Optional, format: HH:mm
                    timeTo: "23:59", // Optional, format: HH:mm
                    isAvailable: false // Required
                };
            });

            const response = await fetchExceptionDays(dataToSend);

            if (response?.length) {
                // Refresh chef data to update UI
                await updateChefData();
                toastRef.current?.addToast(
                    getTranslation('schedule.exceptionDaysAdded', language) || 
                    "Exception days added successfully",
                    "success"
                );
                setIsExceptionModalOpen(false);
            } else {
                // If response is empty, still refresh to ensure UI is up to date
                await updateChefData();
            }
        } catch (error) {
            console.error("Failed to update exception days:", error);
            
            // Extract user-friendly error message
            let errorMessage = getTranslation('schedule.exceptionDaysError', language) || 
                "Failed to add exception days. Please try again.";
            
            // Check for specific error codes/messages
            if (error.response?.data) {
                const errorData = error.response.data;
                const errorCode = errorData.code;
                const errorMsg = errorData.message || errorData.developerMessage;
                
                // Handle specific error codes
                if (errorCode === 100006 || errorMsg?.includes('invalid-chef-exceptional-day')) {
                    errorMessage = getTranslation('schedule.invalidExceptionDay', language) || 
                        "Invalid exception day. The date may already be set as an exception or may be invalid. Please check the date and try again.";
                } else if (errorMsg) {
                    // Use backend error message if available
                    errorMessage = errorMsg;
                }
            }
            
            toastRef.current?.addToast(errorMessage, "error");
        } finally {
            setIsSubmitting(false);
        }
    };


    // Generate time options with half-hour intervals
    const generateTimeOptions = () => {
        const options = [];
        for (let hour = 0; hour < 24; hour++) {
            const hourStr = hour.toString().padStart(2, '0');
            options.push({ value: `${hourStr}:00`, label: `${hourStr}:00` });
            options.push({ value: `${hourStr}:30`, label: `${hourStr}:30` });
        }
        return options;
    };

    const timeOptions = generateTimeOptions();

    // Format date with translated month names - compact format for table display
    const formatDateWithTranslation = (dateString) => {
        const date = new Date(dateString);
        const monthNames = {
            'am': ['Հունվ', 'Փետր', 'Մարտ', 'Ապր', 'Մայ', 'Հուն', 'Հուլ', 'Օգոս', 'Սեպ', 'Հոկ', 'Նոյ', 'Դեկ'],
            'ru': ['Янв', 'Фев', 'Мар', 'Апр', 'Май', 'Июн', 'Июл', 'Авг', 'Сен', 'Окт', 'Ноя', 'Дек'],
            'en': ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
        };
        const month = monthNames[language]?.[date.getMonth()] || monthNames['en'][date.getMonth()];
        const day = date.getDate().toString().padStart(2, '0');
        return `${day} ${month} ${date.getFullYear()}`;
    };

    // Filter exception days to only show future dates
    const filteredExceptionDays = chefAvailabilityExceptionDayDtoList.filter(exception => {
        const exceptionDate = new Date(exception.exceptionDate);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        return exceptionDate >= today;
    });

    const handleRemoveExceptionDay = (exceptionId) => {
        setExceptionIdToDelete(exceptionId);
    };

    const confirmDeleteExceptionDay = async () => {
        if (!exceptionIdToDelete) return;
        
        try {
            setIsSubmitting(true);
            const response = await deleteExceptionDay(exceptionIdToDelete);
            if (response) {
                // Refresh chef data to update UI
                await updateChefData();
                toastRef.current?.addToast(
                    getTranslation('schedule.exceptionDayDeleted', language) || 
                    "Exception day deleted successfully",
                    "success"
                );
                setExceptionIdToDelete(null);
            } else {
                // If response is falsy, still refresh to ensure UI is up to date
                await updateChefData();
            }
        } catch (error) {
            console.error("Failed to delete exception day:", error);
            toastRef.current?.addToast(
                getTranslation('schedule.deleteError', language) || 
                "Failed to delete exception day. Please try again.",
                "error"
            );
        } finally {
            setIsSubmitting(false);
        }
    };

    // Show loading spinner while auth or schedule data is being fetched (standardized pattern)
    if (isAuthLoading || loading) {
        return (
            <div className="pl-2">
                <LoadingSpinner />
            </div>
        );
    }

    // Show error display if there's an error (standardized pattern - same as other pages)
    if (error) {
        return (
            <div className="pl-2">
                <ErrorDisplay
                    error={error}
                    onRetry={async () => {
                        setError(null);
                        setLoading(true);
                        try {
                            await refreshChefData();
                            setError(null);
                        } catch (err) {
                            setError(err);
                        } finally {
                            setLoading(false);
                        }
                    }}
                />
            </div>
        );
    }

    // If no chefData and no error, still show loading (shouldn't happen but safety check)
    if (!chefData) {
        return (
            <div className="pl-2">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="pl-2">
            <Toast ref={toastRef} />
            <h1 className="text-xl font-bold mb-4 text-gray-900 dark:text-[#c9d1d9]">{getTranslation('schedule.title', language) || 'Schedule'}</h1>
            <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-sm border dark:border-[#30363d] p-5 mb-8" style={{ position: 'relative' }}>
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-[#c9d1d9]">{getTranslation('schedule.workingDays', language)}</h2>
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={() => setIsModalOpen(true)}
                        className="font-bold"
                    >
                        {chefAvailableDtoList.length > 0 ? (
                            <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"></path>
                                </svg>
                                {getTranslation('common.edit', language)}
                            </>
                        ) : (
                            <>
                                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                                </svg>
                                {getTranslation('common.add', language)}
                            </>
                        )}
                    </Button>
                </div>
                
                {chefAvailableDtoList.length > 0 ? (
                    <div className="mt-4 mb-4">
                        <h3 className="text-sm font-medium mb-3 text-gray-700 dark:text-[#b1bac4]">{getTranslation('schedule.currentWorkingDays', language)}</h3>
                        <div className="flex flex-wrap gap-3">
                            {chefAvailableDtoList
                                .sort((a, b) => a.dayOfTheWeek - b.dayOfTheWeek)
                                .map((day) => (
                                    <div
                                        key={day.dayOfTheWeek}
                                        className="px-4 py-2 bg-white dark:bg-[#0d1117] rounded-md text-sm border border-gray-200 dark:border-[#30363d] hover:bg-gray-50 dark:hover:bg-[#161b22] transition-colors shadow-sm text-gray-900 dark:text-[#c9d1d9] font-medium"
                                    >
                                        {DaysTranslations[day.dayOfTheWeek][language]} ({day.timeFrom || "09:00"} - {day.timeTo || "18:00"})
                                    </div>
                                ))}
                        </div>
                    </div>
                ) : (
                    <div className="my-4 p-4 bg-white dark:bg-[#0d1117] rounded-md text-center border dark:border-[#30363d]">
                        <p className="text-sm text-gray-500 dark:text-[#8b949e] italic font-medium">{getTranslation('schedule.noWorkingDays', language)}</p>
                    </div>
                )}

                <Modal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    title={getTranslation('schedule.workingDays', language)}
                    size="md"
                >
                    <div className="space-y-4">
                        <p className="text-sm text-gray-600 dark:text-[#8b949e] font-medium mb-4">
                            {getTranslation('schedule.selectWorkingDays', language)}
                        </p>
                        
                        <div className="max-h-[400px] overflow-y-auto pr-2">
                            {Days.map((day, index) => {
                                const isSelected = selectedDays.some(d => d.dayOfTheWeek === index);
                                const selectedDay = selectedDays.find(d => d.dayOfTheWeek === index);

                                return (
                                    <div key={index} className="mb-3">
                                        <div
                                            className="cursor-pointer px-4 py-3 flex justify-between items-center hover:bg-gray-50 dark:hover:bg-[#1c2128] rounded-md transition-colors text-gray-900 dark:text-[#c9d1d9] font-medium border border-gray-200 dark:border-[#30363d]"
                                            onClick={() => handleSelectDay(index)}
                                        >
                                            <span>{DaysTranslations[index][language]}</span>
                                            <div className="w-5 h-5 border dark:border-[#30363d] rounded flex items-center justify-center bg-white dark:bg-[#0d1117]">
                                                {isSelected && <img src={check || ""} alt="check" className="w-4 h-4 dark:invert dark:opacity-80"/>}
                                            </div>
                                        </div>
                                        {isSelected && selectedDay && (
                                            <div className={`px-4 py-3 mt-2 bg-white dark:bg-[#0d1117] rounded-md border ${validationErrors[index] ? 'border-red-500 dark:border-red-400' : 'border-gray-200 dark:border-[#30363d]'}`}>
                                                <div className="flex items-center gap-3">
                                                    <label className="text-sm text-gray-700 dark:text-[#b1bac4] font-medium whitespace-nowrap">
                                                        {getTranslation('schedule.from', language) || 'From'}:
                                                    </label>
                                                <div className="w-[100px]">
                                                    <Select
                                                        value={timeOptions.find(opt => opt.value === (selectedDay.timeFrom || "09:00"))}
                                                        onChange={(option) => handleTimeChange(index, 'timeFrom', option.value)}
                                                        options={timeOptions}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        styles={{
                                                            control: (provided, state) => ({
                                                                ...provided,
                                                                minHeight: '36px',
                                                                height: '36px',
                                                                paddingRight: '8px', // Consistent padding to move arrow away from right edge
                                                                backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
                                                                borderColor: validationErrors[index]
                                                                    ? '#f56565'
                                                                    : (state.isFocused 
                                                                        ? (isDarkMode ? '#30363d' : '#cfcfcf')
                                                                        : (isDarkMode ? '#30363d' : '#e5e7eb')),
                                                                boxShadow: validationErrors[index]
                                                                    ? '0 0 0 1px #f56565'
                                                                    : (state.isFocused 
                                                                        ? (isDarkMode ? '0 0 0 1px #30363d' : '0 0 0 1px #cfcfcf')
                                                                        : 'none'),
                                                                '&:hover': {
                                                                    borderColor: validationErrors[index] 
                                                                        ? '#f56565' 
                                                                        : (isDarkMode ? '#30363d' : '#cfcfcf')
                                                                }
                                                            }),
                                                            singleValue: (provided) => ({
                                                                ...provided,
                                                                color: isDarkMode ? '#c9d1d9' : '#374151',
                                                                fontSize: '14px',
                                                                fontWeight: '500'
                                                            }),
                                                            menu: (provided) => ({
                                                                ...provided,
                                                                zIndex: 10000,
                                                                backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
                                                                borderColor: isDarkMode ? '#30363d' : '#e5e7eb'
                                                            }),
                                                            menuPortal: (provided) => ({
                                                                ...provided,
                                                                zIndex: 10000
                                                            }),
                                                            option: (provided, state) => ({
                                                                ...provided,
                                                                backgroundColor: state.isSelected
                                                                    ? (isDarkMode ? '#1c2128' : '#f3f4f6')
                                                                    : state.isFocused
                                                                        ? (isDarkMode ? '#1c2128' : '#f3f4f6')
                                                                        : (isDarkMode ? '#0d1117' : '#ffffff'),
                                                                color: isDarkMode ? '#c9d1d9' : '#374151',
                                                                fontSize: '14px',
                                                                '&:hover': {
                                                                    backgroundColor: isDarkMode ? '#1c2128' : '#f3f4f6'
                                                                }
                                                            }),
                                                            dropdownIndicator: (provided) => ({
                                                                ...provided,
                                                                color: isDarkMode ? '#8b949e' : '#6b7280',
                                                                padding: '4px'
                                                            })
                                                        }}
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            colors: {
                                                                ...theme.colors,
                                                                primary: '#3b82f6',
                                                                neutral0: isDarkMode ? '#0d1117' : '#ffffff',
                                                                neutral20: isDarkMode ? '#30363d' : '#e5e7eb',
                                                            }
                                                        })}
                                                    />
                                                </div>
                                                <span className="text-gray-700 dark:text-[#b1bac4] font-medium">-</span>
                                                <label className="text-sm text-gray-700 dark:text-[#b1bac4] font-medium whitespace-nowrap">
                                                    {getTranslation('schedule.to', language) || 'To'}:
                                                </label>
                                                <div className="w-[100px]">
                                                    <Select
                                                        value={timeOptions.find(opt => opt.value === (selectedDay.timeTo || "18:00"))}
                                                        onChange={(option) => handleTimeChange(index, 'timeTo', option.value)}
                                                        options={timeOptions}
                                                        menuPortalTarget={document.body}
                                                        menuPosition="fixed"
                                                        styles={{
                                                            control: (provided, state) => ({
                                                                ...provided,
                                                                minHeight: '36px',
                                                                height: '36px',
                                                                paddingRight: '8px', // Consistent padding to move arrow away from right edge
                                                                backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
                                                                borderColor: validationErrors[index]
                                                                    ? '#f56565'
                                                                    : (state.isFocused 
                                                                        ? (isDarkMode ? '#30363d' : '#cfcfcf')
                                                                        : (isDarkMode ? '#30363d' : '#e5e7eb')),
                                                                boxShadow: validationErrors[index]
                                                                    ? '0 0 0 1px #f56565'
                                                                    : (state.isFocused 
                                                                        ? (isDarkMode ? '0 0 0 1px #30363d' : '0 0 0 1px #cfcfcf')
                                                                        : 'none'),
                                                                '&:hover': {
                                                                    borderColor: validationErrors[index] 
                                                                        ? '#f56565' 
                                                                        : (isDarkMode ? '#30363d' : '#cfcfcf')
                                                                }
                                                            }),
                                                            singleValue: (provided) => ({
                                                                ...provided,
                                                                color: isDarkMode ? '#c9d1d9' : '#374151',
                                                                fontSize: '14px',
                                                                fontWeight: '500'
                                                            }),
                                                            menu: (provided) => ({
                                                                ...provided,
                                                                zIndex: 10000,
                                                                backgroundColor: isDarkMode ? '#0d1117' : '#ffffff',
                                                                borderColor: isDarkMode ? '#30363d' : '#e5e7eb'
                                                            }),
                                                            menuPortal: (provided) => ({
                                                                ...provided,
                                                                zIndex: 10000
                                                            }),
                                                            option: (provided, state) => ({
                                                                ...provided,
                                                                backgroundColor: state.isSelected
                                                                    ? (isDarkMode ? '#1c2128' : '#f3f4f6')
                                                                    : state.isFocused
                                                                        ? (isDarkMode ? '#1c2128' : '#f3f4f6')
                                                                        : (isDarkMode ? '#0d1117' : '#ffffff'),
                                                                color: isDarkMode ? '#c9d1d9' : '#374151',
                                                                fontSize: '14px',
                                                                '&:hover': {
                                                                    backgroundColor: isDarkMode ? '#1c2128' : '#f3f4f6'
                                                                }
                                                            }),
                                                            dropdownIndicator: (provided) => ({
                                                                ...provided,
                                                                color: isDarkMode ? '#8b949e' : '#6b7280',
                                                                padding: '4px'
                                                            })
                                                        }}
                                                        theme={(theme) => ({
                                                            ...theme,
                                                            colors: {
                                                                ...theme.colors,
                                                                primary: '#3b82f6',
                                                                neutral0: isDarkMode ? '#0d1117' : '#ffffff',
                                                                neutral20: isDarkMode ? '#30363d' : '#e5e7eb',
                                                            }
                                                        })}
                                                    />
                                                </div>
                                                </div>
                                                {validationErrors[index] && (
                                                    <p className="text-red-500 dark:text-red-400 text-xs mt-2 font-medium">
                                                        {validationErrors[index]}
                                                    </p>
                                                )}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className="flex gap-3 pt-4 border-t dark:border-[#30363d]">
                            <Button
                                variant="secondary"
                                size="md"
                                type="button"
                                onClick={() => setIsModalOpen(false)}
                                className="flex-1 font-semibold"
                            >
                                {getTranslation('common.cancel', language) || 'Cancel'}
                            </Button>
                            <Button
                                variant="primary"
                                size="md"
                                type="button"
                                    onClick={handleSubmit}
                                    disabled={isSubmitting}
                                loading={isSubmitting}
                                className="flex-1 font-semibold"
                                >
                                {getTranslation('common.confirm', language)}
                            </Button>
                        </div>
                </div>
                </Modal>
            </div>
            <CompanySchedules toastRef={toastRef} />
            <div className="bg-white dark:bg-[#161b22] rounded-lg shadow-sm border dark:border-[#30363d] p-5">
                <div className="flex justify-between items-center mb-4">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-[#c9d1d9]">{getTranslation('schedule.exceptionDays', language)}</h2>
                    <Button
                        variant="secondary"
                        size="md"
                        onClick={() => setIsExceptionModalOpen(true)}
                        className="font-bold"
                    >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6"></path>
                        </svg>
                        {getTranslation('common.add', language)}
                    </Button>
                </div>
                <p className="text-sm text-gray-600 dark:text-[#8b949e] mb-4 font-medium">{getTranslation('schedule.exceptionDaysDescription', language)}</p>
                
                {filteredExceptionDays.length > 0 ? (
                    <div className="mt-4 mb-8">
                        <h3 className="text-sm font-medium mb-3 text-gray-700 dark:text-[#b1bac4]">{getTranslation('schedule.currentExceptionDays', language)}</h3>
                        <div className="overflow-x-auto max-h-[300px] overflow-y-auto">
                            <table className="min-w-full bg-white dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-md">
                                <thead className="sticky top-0 bg-white dark:bg-[#161b22]">
                                    <tr className="bg-white dark:bg-[#0d1117]">
                                        <th className="py-2 px-4 border-b dark:border-[#30363d] text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">{getTranslation('common.date', language)}</th>
                                        <th className="py-2 px-4 border-b dark:border-[#30363d] text-left text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">{getTranslation('common.available', language)}</th>
                                        <th className="py-2 px-4 border-b dark:border-[#30363d] text-xs font-medium text-gray-500 dark:text-[#8b949e] uppercase tracking-wider">{getTranslation('common.action', language)}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredExceptionDays.map((exception, idx) => (
                                        <tr key={idx} className="hover:bg-gray-50 dark:hover:bg-[#1c2128]">
                                            <td className="py-2 px-4 border-b dark:border-[#30363d] text-sm text-gray-900 dark:text-[#c9d1d9] font-medium">
                                                {formatDateWithTranslation(exception.exceptionDate)}
                                            </td>
                                            <td className="py-2 px-4 border-b dark:border-[#30363d] text-sm text-gray-700 dark:text-[#b1bac4] font-medium">
                                                {exception.isAvailable ? getTranslation('common.yes', language) : getTranslation('common.no', language)}
                                            </td>
                                            <td className="py-2 px-4 border-b dark:border-[#30363d] text-sm text-center">
                                                <button 
                                                    className="text-gray-700 dark:text-[#b1bac4] hover:text-red-600 dark:hover:text-red-400 transition-colors"
                                                    onClick={() => handleRemoveExceptionDay(exception.id)}
                                                    disabled={isSubmitting}
                                                >
                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" strokeWidth={2.5}>
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path>
                                                    </svg>
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="my-4 p-4 bg-white dark:bg-[#0d1117] rounded-md text-center border dark:border-[#30363d]">
                        <p className="text-sm text-gray-500 dark:text-[#8b949e] italic font-medium">{getTranslation('schedule.noExceptionDays', language)}</p>
                    </div>
                )}
                
                <Modal
                    isOpen={isExceptionModalOpen}
                    onClose={() => setIsExceptionModalOpen(false)}
                    title={getTranslation('schedule.addNewException', language)}
                    size="4xl"
                >
                        <DoubleCalendar 
                        onClose={() => setIsExceptionModalOpen(false)} 
                            onDateSelect={onDateSelect} 
                            chefAvailabilityExceptionDayDtoList={chefAvailabilityExceptionDayDtoList}
                        showQuickSelect={false}
                        />
                </Modal>

                {exceptionIdToDelete && (
                    <ConfirmationModal
                        description={getTranslation('schedule.confirmDeleteException', language) || 
                            "Are you sure you want to delete the exception day?"}
                        onCancel={() => setExceptionIdToDelete(null)}
                        onSubmit={confirmDeleteExceptionDay}
                    />
                )}
            </div>
        </div>
    );
};

export default Schedule;
